import React, { useState, useEffect } from 'react';
import { Typography, Card, Space, Button, Breadcrumb, Divider, Spin } from 'antd';
import { showConfirmModal } from '../components/PopupModal';
import { useNavigate, useParams } from 'react-router-dom';
import { usersApi } from '../services/users';
import { message } from '../utils/message';

const { Title, Text } = Typography;

interface PermissionDetailData {
  id: string;
  manager: string;
  managerId: string;
  password: string;
  email: string;
  phone: string;
  permission: string;
  registrationDate: string;
  lastLoginDate: string;
  lastUpdateDate: string;
}

const PermissionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id: permissionId } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [permissionData, setPermissionData] = useState<PermissionDetailData | null>(null);

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\./g, '.').slice(0, -1);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/\./g, '.').slice(0, -1);
  };

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '-';
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    // 010-XXXX-XXXX 형식으로 변환
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  };

  // 데이터 로드
  useEffect(() => {
    const loadPermissionData = async () => {
      if (!permissionId) {
        navigate('/permission-management');
        return;
      }

      setLoading(true);
      try {
        const response = await usersApi.getUser(Number(permissionId));

        if (response.success) {
          const user = response.data;
          const transformedData: PermissionDetailData = {
            id: user.id.toString(),
            manager: user.name,
            managerId: user.username,
            password: '••••••••', // 보안상 마스킹 처리
            email: user.email,
            phone: formatPhoneNumber(user.phone),
            permission: user.role === 'super_admin' ? 'Super Admin' : 'Admin',
            registrationDate: formatDate(user.createdAt),
            lastLoginDate: formatDateTime(user.lastLogin),
            lastUpdateDate: formatDateTime(user.updatedAt)
          };

          setPermissionData(transformedData);
        } else {
          message.error('사용자 정보를 불러오는데 실패했습니다.');
          navigate('/permission-management');
        }
      } catch (error: any) {
        console.error('사용자 상세 정보 로드 실패:', error);
        message.error('사용자 정보를 불러오는데 실패했습니다.');
        navigate('/permission-management');
      } finally {
        setLoading(false);
      }
    };

    loadPermissionData();
  }, [permissionId, navigate]);

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate('/permission-management');
  };

  // 수정 핸들러
  const handleEdit = () => {
    navigate(`/permission-management/edit/${permissionId}`);
  };

  // 삭제 핸들러
  const handleDelete = () => {
    if (!permissionData) return;

    showConfirmModal({
      title: '사용자 삭제',
      content: `정말로 '${permissionData.manager}' 사용자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onConfirm: async () => {
        try {
          const response = await usersApi.deleteUser(Number(permissionId));

          if (response.success) {
            message.success('삭제가 완료되었습니다.');
            navigate('/permission-management');
          } else {
            message.error('사용자 삭제에 실패했습니다.');
          }
        } catch (error: any) {
          console.error('사용자 삭제 실패:', error);
          message.error('사용자 삭제 중 오류가 발생했습니다.');
        }
      }
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!permissionData) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>데이터를 찾을 수 없습니다.</Text>
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '권한 관리', href: '/permission-management' },
          { title: '권한 상세' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 0 32px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
            권한 상세
          </Title>
        </div>
      </div>

      {/* 기본 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기본 정보</Text>}
        style={{
          marginBottom: '24px',
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* 관리자명 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>관리자명</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.manager}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* ID */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>ID</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.managerId}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 비밀번호 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>비밀번호</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.password}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 이메일 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>이메일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.email}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 휴대전화 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>휴대전화</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.phone}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 권한 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>권한</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.permission}</Text>
          </div>
        </div>
      </Card>

      {/* 기타 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기타 정보</Text>}
        style={{
          marginBottom: '24px',
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* 최초등록일 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>최초등록일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.registrationDate}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 마지막 로그인 일시 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>마지막 로그인 일시</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.lastLoginDate}</Text>
          </div>

          <Divider style={{ margin: '0', borderColor: '#303030' }} />

          {/* 마지막 수정 일시 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>마지막 수정 일시</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>{permissionData.lastUpdateDate}</Text>
          </div>
        </div>
      </Card>

      {/* 하단 버튼 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px'
      }}>
        <Button
          size="large"
          onClick={handleDelete}
          style={{
            minWidth: '100px',
            background: 'transparent',
            border: '1px solid #ff4d4f',
            color: '#ff4d4f'
          }}
        >
          삭제
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleEdit}
          style={{
            minWidth: '100px',
            background: '#1890ff',
            border: 'none'
          }}
        >
          수정
        </Button>
      </div>
    </Space>
  );
};

export default PermissionDetail;